import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useValidateInvitation } from "@/hooks";
import { Button } from "@/components/ui/button";
import { GoogleAuthButton } from "@/components/button/auth-buttons";
import type { CompanyInvitation } from "@/types/user.types";
import type { InvitationValidationResponse } from "@/services/invitation/invitation.service";

export default function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationValidationResponse | null>(null);

  const validateInvitationMutation = useValidateInvitation();

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setIsValid(false);
      return;
    }

    validateInvitationMutation.mutateAsync(token)
      .then((data: InvitationValidationResponse) => {
        setIsValid(true);
        setInvitationData(data);
      })
      .catch(() => {
        setIsValid(false);
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [token]);

  const handleGoogleAuth = () => {
    if (token) {
      const googleAuthUrl = `${import.meta.env.VITE_API_URL || ""}/auth/google?invitationToken=${token}`;
      window.location.href = googleAuthUrl;
    } else {
      window.location.href = `${import.meta.env.VITE_API_URL || ""}/auth/google`;
    }
  };

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!token || !isValid) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <h1 className="text-2xl font-bold mb-4">{t("onboarding.invitation.invalid")}</h1>
          <p className="text-gray-600 mb-6">{t("onboarding.invitation.invalidDescription")}</p>
          <Link to="/auth/register">
            <Button>{t("onboarding.invitation.createAccount")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8">
        <h1 className="text-2xl font-bold mb-2">{t("onboarding.invitation.title")}</h1>
        <p className="text-gray-600 mb-6">
          {t("onboarding.invitation.description", {
            companyName: invitationData?.company?.name || "the company",
            inviterName: invitationData?.inviter?.firstName || invitationData?.inviter?.email || "someone",
          })}
        </p>

        <div className="space-y-4">
          <Link to={`/auth/register?invitationToken=${token}`} className="block">
            <Button className="w-full">{t("onboarding.invitation.createAccount")}</Button>
          </Link>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">OR</span>
            </div>
          </div>

          <GoogleAuthButton handlePress={handleGoogleAuth} />
        </div>
      </div>
    </div>
  );
}

